<script lang="ts" setup>
import { computed, useSlots } from "vue";
import VButtonBase from "../VButtonBase/VButtonBase.vue";
import type { IButtonProps, IButtonEmits } from "./VButton.types";
import { responsiveClass } from "../../utilities/helpers";

const props = withDefaults(defineProps<IButtonProps>(), {
  theme: "primary",
  iconPosition: undefined,
});
const emit = defineEmits<IButtonEmits>();

const slots = useSlots();

const hasButtonText = computed(() => {
  return !!slots.default;
});
const hasIcon = computed(() => {
  return !!slots.icon;
});

const rootClass = computed(() => [
  {
    "pl-button": true,
    [`pl-button_${props.theme}`]: props.theme,
    ["pl-button_shadow"]: props.shadow,
    ["pl-button_rounded"]:
      props.rounded && !(hasButtonText.value || props.description),
    [`pl-button_icon-only`]: !hasButtonText.value,
    [`pl-button_icon-${props.iconPosition}`]:
      hasIcon.value && props.iconPosition,
  },
  ...responsiveClass("pl-button_square", props.square),
]);

const onClick = (event: Event) => {
  emit("click", event);
};
</script>

<template>
  <VButtonBase
    :tag="tag"
    :full-width="fullWidth"
    :class="rootClass"
    :disabled="disabled"
    :loading="loading"
    :type="type"
    :size="size"
    @click="onClick"
  >
    <span
      v-if="hasButtonText || (description && size !== 's')"
      class="pl-button__text"
    >
      <span v-if="hasButtonText" class="pl-button__title"><slot /></span>
      <span v-if="description && size !== 's'" class="pl-button__description">{{
        description
      }}</span>
    </span>
    <span v-if="hasIcon" class="pl-button__icon">
      <slot name="icon" />
    </span>
  </VButtonBase>
</template>

<style scoped lang="postcss">
.pl-button {
  --pl-button-display: block;

  .pl-button_icon-only& {
    padding: var(--_button-padding-only-icon);
  }

  &_shadow {
    box-shadow: var(--pl-shadow-ml);

    @media (hover: hover) {
      &:hover {
        box-shadow: var(--pl-shadow-m);
      }
    }

    @media (hover: none) {
      &:active {
        box-shadow: var(--pl-shadow-s);
      }
    }

    &.pl-button-base_disabled {
      box-shadow: var(--pl-shadow-ml);
    }
  }

  &_rounded {
    border-radius: 50%;
  }

  &_primary {
    color: var(--pl-text-invert-primary);
    background-color: var(--pl-button-background-primary-default);

    @media (hover: hover) {
      &:hover {
        background-color: var(--pl-button-background-primary-hover);
      }
    }

    @media (hover: none) {
      &:active {
        background-color: var(--pl-button-background-primary-pressed);
      }
    }
  }

  &_primary.pl-button-base_loading {
    &,
    &:hover,
    &:active {
      background-color: var(--pl-button-background-primary-default);
    }
  }

  &_primary.pl-button-base_disabled {
    &,
    &:hover,
    &:active {
      background-color: var(--pl-button-background-primary-disabled);
    }
  }

  &_secondary {
    color: var(--pl-text-primary);
    background-color: var(--pl-button-background-secondary-default);

    @media (hover: hover) {
      &:hover {
        background-color: var(--pl-button-background-secondary-hover);
      }
    }

    @media (hover: none) {
      &:active {
        background-color: var(--pl-button-background-secondary-pressed);
      }
    }
  }

  &_secondary.pl-button-base_loading {
    &,
    &:hover,
    &:active {
      background-color: var(--pl-button-background-secondary-default);
    }
  }

  &_secondary.pl-button-base_disabled {
    &,
    &:hover,
    &:active {
      color: var(--pl-text-tertiary);
      background-color: var(--pl-button-background-secondary-disabled);
    }
  }

  &_border {
    color: var(--pl-text-primary);
    background-color: var(--pl-button-background-tertiary-default);
    border: 1px solid var(--pl-button-border-default);

    @media (hover: hover) {
      &:hover {
        border-color: var(--pl-button-border-hover);
        background-color: var(--pl-button-background-tertiary-hover);
      }
    }

    @media (hover: none) {
      &:active {
        border-color: var(--pl-button-border-pressed);
        background-color: var(--pl-button-background-tertiary-pressed);
      }
    }
  }

  &_border.pl-button-base_loading {
    &,
    &:hover,
    &:active {
      border-color: var(--pl-button-border-default);
    }
  }

  &_border.pl-button-base_disabled {
    &,
    &:hover,
    &:active {
      color: var(--pl-text-tertiary);
      border-color: var(--pl-button-border-disabled);
    }
  }

  &_tertiary {
    color: var(--pl-text-primary);
    background-color: var(--pl-button-background-tertiary-default);

    @media (hover: hover) {
      &:hover {
        background-color: var(--pl-button-background-tertiary-hover);
      }
    }

    @media (hover: none) {
      &:active {
        background-color: var(--pl-button-background-tertiary-pressed);
      }
    }
  }

  &_tertiary.pl-button-base_loading {
    &,
    &:hover,
    &:active {
      color: var(--pl-text-primary);
      background-color: var(--pl-button-background-tertiary-default);
    }
  }

  &_tertiary.pl-button-base_disabled {
    &,
    &:hover,
    &:active {
      color: var(--pl-text-tertiary);
      background-color: var(--pl-button-background-tertiary-disabled);
    }
  }

  &_invert {
    color: var(--pl-text-primary);
    background-color: var(--pl-button-background-invert-primary-default);

    @media (hover: hover) {
      &:hover {
        background-color: var(--pl-button-background-invert-primary-hover);
      }
    }

    @media (hover: none) {
      &:active {
        background-color: var(--pl-button-background-invert-primary-pressed);
      }
    }
  }

  &_invert.pl-button-base_loading {
    &,
    &:hover,
    &:active {
      color: var(--pl-text-primary);
      background-color: var(--pl-button-background-invert-primary-default);
    }
  }

  &_invert.pl-button-base_disabled {
    &,
    &:hover,
    &:active {
      color: var(--pl-text-tertiary);
      background-color: var(--pl-button-background-invert-primary-disabled);
    }
  }

  &_invert-tertiary {
    color: var(--pl-text-invert-primary);
    background-color: var(--pl-button-background-invert-tertiary-default);

    @media (hover: hover) {
      &:hover {
        background-color: var(--pl-button-background-invert-tertiary-hover);
      }
    }

    @media (hover: none) {
      &:active {
        background-color: var(--pl-button-background-invert-tertiary-pressed);
      }
    }
  }

  &_invert-tertiary.pl-button-base_loading {
    &,
    &:hover,
    &:active {
      color: var(--pl-text-invert-primary);
      background-color: var(--pl-button-background-invert-tertiary-default);
    }
  }

  &_invert-tertiary.pl-button-base_disabled {
    &,
    &:hover,
    &:active {
      color: var(--pl-text-invert-secondary);
      background-color: var(--pl-button-background-invert-tertiary-disabled);
    }
  }

  &_neutral {
    color: var(--pl-text-invert-primary);
    background-color: var(--pl-button-background-neutral-default);

    @media (hover: hover) {
      &:hover {
        background-color: var(--pl-button-background-neutral-hover);
      }
    }

    @media (hover: none) {
      &:active {
        background-color: var(--pl-button-background-neutral-pressed);
      }
    }
  }

  &_neutral.pl-button-base_loading {
    &,
    &:hover,
    &:active {
      color: var(--pl-text-invert-primary);
      background-color: var(--pl-button-background-neutral-default);
    }
  }

  &_neutral.pl-button-base_disabled {
    &,
    &:hover,
    &:active {
      color: var(--pl-text-invert-secondary);
      background-color: var(--pl-button-background-neutral-disabled);
    }
  }

  &__icon {
    align-items: center;
    display: flex;
    justify-content: center;

    .pl-button_icon-left & {
      order: -1;
    }
  }

  &__description {
    font: var(--_button-font-description);
  }

  &__title,
  &__description {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__text {
    display: flex;
    flex-direction: column;

    .pl-button_icon-left &,
    .pl-button_icon-right & {
      padding: 0 8px;
    }
  }

  &_square {
    padding: var(--_button-padding-only-icon);

    .pl-button__text {
      display: none;
    }
  }

  @each $breakpoint in s, m, ml, l, xl {
    @media (--pl-viewport-from-$(breakpoint)) {
      &_square-true-$(breakpoint) {
        &:not(.pl-button_icon-only) {
          padding: var(--_button-padding-only-icon);
        }

        .pl-button__text {
          display: none;
        }
      }

      &_square-false-$(breakpoint) {
        &:not(.pl-button_icon-only) {
          padding: var(--_button-padding);
        }

        .pl-button__text {
          display: var(--pl-button-display);
        }
      }
    }
  }
}
</style>
